<template>
  <YapModal v-if="hasMultipleAccount">
    <template #trigger="{ openModal }">
      <YapButtonIcon @click="openSwitcher(openModal)" data-testid="context-switcher-trigger">
        <YapIcon name="company-outline" class="h-6 w-6" />
      </YapButtonIcon>
    </template>
    <template #title>{{ t('app.partnership.partnershipContext.title') }}</template>
    <template #default="{ closeModal }">
      <div
        v-if="state.switchingContext || state.loading"
        class="absolute -mx-3 -my-3 flex h-full w-full items-center justify-center bg-yapgray-100/60 backdrop-blur firefox:bg-yapgray-100 dark:bg-yapdarkgray-900/80 firefox:dark:bg-yapdarkgray-900"
      >
        <YapLoader :size="YapLoaderSizes.Medium" class="-mt-16" />
      </div>
      <div class="w-full">
        <YapSearchField
          id="searchCtx"
          :placeholder="t('app.partnership.partnershipContext.searchHint')"
          :floating="true"
          :grouped="true"
          :delay="0"
          class="rounded-t-lg"
          @search="(value) => (state.searchQuery = value)"
        />
        <ul
          class="divide-y divide-yapgray-200 overflow-hidden rounded-b-lg border-t bg-white dark:divide-yapdarkgray-500 dark:border-t-yapdarkgray-600 dark:bg-yapdarkgray-900"
        >
          <li
            v-for="account in filteredContexts"
            :key="account.uid"
            class="cursor-pointer list-none border-b dark:border-yapdarkgray-600"
            :class="{
              'cursor-default opacity-50': accountIsDisabled(account.uid)
            }"
            @click="!accountIsDisabled(account.uid) && switchContext(account, closeModal)"
            data-bs-dismiss="modal"
          >
            <AccountEntry
              class="p-4"
              :name="account.name"
              currency="CHF"
              :amount="cashAccountsStore.getCashAccountsBalanceForContext(account.uid)"
              :picture-uri="account.pictureUri"
              :is-clickable="!accountIsDisabled(account.uid)"
            />
          </li>
          <li v-if="filteredContexts.length === 0" class="mt-2 p-2 text-yapgray-800 dark:text-yapgray-500">
            {{ t('app.partnership.partnershipContext.noResults') }}
          </li>
        </ul>
      </div>
    </template>
  </YapModal>
</template>

<script setup lang="ts">
import { computed, reactive } from 'vue';

import router from '@/router/router';
import { useI18n } from '@/plugins/i18n';
import YapIcon from '@/components/YapIcon.vue';
import YapModal from '@/components/YapModal.vue';
import { useUserStore } from '@/store/userStore';
import { PartnershipStatus } from '@/types/partnership';
import YapButtonIcon from '@/components/YapButtonIcon.vue';
import YapSearchField from '@/components/YapSearchField.vue';
import { useCashAccountsStore } from '@/store/cashAccountsStore';
import { usePartnershipsStore } from '@/store/partnershipsStore';
import AccountEntry from '@/components/sideMenu/AccountEntry.vue';
import { createResourceUri, getUidFromSelfLink } from '@/utils/links';
import YapLoader, { YapLoaderSizes } from '@/components/YapLoader.vue';
import { ContextSwitch, useContextSwitchStore } from '@/store/contextSwitchStore';
import { NaturalPersonURI } from '@/types/naturalPerson';
import { RouteNames } from '@/router/routes';

const { t } = useI18n();
const userStore = useUserStore();
const cashAccountsStore = useCashAccountsStore();
const partnershipsStore = usePartnershipsStore();
const hasMultipleAccount = computed(() => partnershipsStore.hasPartnerships);

const contextSwitchStore = useContextSwitchStore();
const filteredContexts = computed(() =>
  contextSwitchStore.getContexts
    .filter((ctx) => ctx.uid !== contextSwitchStore.currentContextUid)
    .filter((ctx) => ctx.name.toLocaleLowerCase().includes(state.searchQuery.toLocaleLowerCase()))
    .sort((a, b) => {
      // We want the private context to always be on top
      if (a.uid === userStore.uid) return -1;
      if (b.uid === userStore.uid) return 1;
      return a.name.localeCompare(b.name);
    })
);

interface State {
  loading: boolean;
  switchingContext: boolean;
  searchQuery: string;
}
const state: State = reactive({
  loading: false,
  switchingContext: false,
  searchQuery: ''
});

function accountIsDisabled(accountUid: string) {
  return userStore.identificationOnly && accountUid === userStore.uid;
}

async function openSwitcher(openModal: any) {
  state.loading = true;
  state.searchQuery = '';
  openModal();
  await loadPartnerships();
  state.loading = false;
}

async function loadPartnerships(forceReload?: boolean) {
  if (userStore.uid.length === 0) {
    console.log('in ContextSwitcher->loadPartnerships no user uid...');

    return;
  }
  if (!forceReload && partnershipsStore.partnerships.size > 0) {
    return;
  }
  await partnershipsStore.loadPartnerships(
    createResourceUri(NaturalPersonURI.BaseV2, {
      uid: userStore.uid,
      resource: NaturalPersonURI.PartnershipsEngagements,
      queryParams: {
        noConstitution: true,
        partnershipStatus: [
          PartnershipStatus.New,
          PartnershipStatus.Registered,
          PartnershipStatus.Screened,
          PartnershipStatus.Identified,
          PartnershipStatus.Bankable,
          PartnershipStatus.Passive,
          PartnershipStatus.Cancelling
        ]
      }
    })
  );
  contextSwitchStore.loadPartnershipContexts();
}

async function switchContext(context: ContextSwitch, closeModal: any) {
  state.switchingContext = true;
  router.push({ name: RouteNames.Dashboard });
  contextSwitchStore.setCurrentContext(context.uid);
  await loadCashAccountsForCurrentContext();
  closeModal();
  state.switchingContext = false;
}

/**
 * Load the cash-accounts for the current context and select the defaultCashAccount.
 * Regardless of the current context, we also load the private cash-account in order to enrich the user data with it's total valuation.
 */
async function loadCashAccountsForCurrentContext() {
  if (!contextSwitchStore.isPrivateContext) {
    await partnershipsStore.loadCurrentPartnershipAndCashAccounts();
  }
  cashAccountsStore
    .loadCashAccounts(
      createResourceUri(NaturalPersonURI.Base, { uid: userStore.uid, resource: NaturalPersonURI.CashAccounts })
    )
    .then(async () => {
      const privateCashAccounts = cashAccountsStore.getPrivateCashAccounts();
      const defaultPrivateCashAccount = privateCashAccounts[0];
      if (contextSwitchStore.isPrivateContext) {
        cashAccountsStore.selectCashAccount(getUidFromSelfLink(defaultPrivateCashAccount._links.self));
      }

      cashAccountsStore.getCashAccountDetails(defaultPrivateCashAccount._links.self);
    })
    .catch((err) => {
      console.error('Error loading cash accounts for private context', err);
    });
}
</script>
